import { Component, ViewChild, inject, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { SchemaFormsService } from '@apexdesigner/schema-forms';
import createDebug from 'debug';
import { firstValueFrom } from 'rxjs';
import { PackageService } from './services/package/package.service';
import { PageService } from './services/page/page.service';
import { ComponentService } from './services/component/component.service';
import { AuthService } from './services/auth/auth.service';
import { TenantService } from './services/tenant/tenant.service';
import { AsyncPipe } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import {
  MatSidenavContainer,
  MatSidenavContent,
} from '@angular/material/sidenav';
import { MatToolbar } from '@angular/material/toolbar';
import { RouterLink, RouterOutlet } from '@angular/router';
import {
  FlexColumnComponent,
  FlexRowComponent,
  GrowDirective,
} from '@apexdesigner/flex-layout';
import { AvatarComponent } from '@components/avatar/avatar.component';
import { TenantChooserDialogComponent } from '@components/tenant-chooser-dialog/tenant-chooser-dialog.component';
import { AdElDirective } from './directives/ad-el/ad-el.directive';

const Debug = createDebug('AppComponent');

/**
 * App
 *
 * Root application component.
 */
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  host: { adComp: '' },
  imports: [
    AdElDirective,
    AsyncPipe,
    AvatarComponent,
    FlexColumnComponent,
    FlexRowComponent,
    GrowDirective,
    MatButton,
    MatIcon,
    MatSidenavContainer,
    MatSidenavContent,
    MatToolbar,
    RouterLink,
    RouterOutlet,
    TenantChooserDialogComponent,
  ],
})
export class AppComponent implements OnInit {
  packageService = inject(PackageService);
  /** Sidenav Open */
  sidenavOpen = false;

  /** Toggles the sidenav and drops focus from the toggle button so it doesn't stay highlighted. */
  toggleSidenav(event: Event) {
    this.sidenavOpen = !this.sidenavOpen;
    (event.currentTarget as HTMLElement).blur();
  }

  pageService = inject(PageService);
  /**
   * Visible Sidenav Pages - Sidenav pages the current user's roles allow.
   * Typed via PageService because the client component generator only carries
   * service-class imports from `@services`, not type-only ones.
   */
  visibleSidenavPages: PageService['sidenavPages'] = [];

  /** Initialize - Recompute the visible sidenav pages whenever the current user changes */

  async initialize(): Promise<void> {
    this.authService.currentUser.subscribe((user) => {
      // Mirrors the roleGuard semantics: a page with no declared roles is
      // visible to any resolved user, the built-in Everyone role is public
      // (the routes generator strips it before the guard ever sees it), and
      // otherwise any one matching role assignment suffices.
      this.visibleSidenavPages = this.pageService.sidenavPages.filter(
        (page) =>
          page.roles.length === 0 ||
          page.roles.includes('Everyone') ||
          page.roles.some((roleName) =>
            user?.roleAssignments?.some(
              (ra: any) => ra.role?.name === roleName,
            ),
          ),
      );
    });
  }

  private schemaFormsService = inject(SchemaFormsService);
  componentService = inject(ComponentService);
  authService = inject(AuthService);
  private http = inject(HttpClient);
  tenantService = inject(TenantService);
  /** Tenant Chooser Dialog */
  @ViewChild('tenantChooserDialog')
  tenantChooserDialog!: TenantChooserDialogComponent;
  /** Show Switch Tenant - Whether tenant switching is available */
  showSwitchTenant = false;
  /** Current Tenant Name - Shown in the header chip */
  currentTenantName = '';

  /** Register Fields - Register schema form fields from component metadata */ registerFields(): void {
    const debug = Debug.extend('registerFields');
    for (const meta of this.componentService.metadata) {
      if (meta.metadata?.schemaFormsField) {
        const fieldName = meta.selector.replace(/-field$/, '');
        this.schemaFormsService.registerField(fieldName, () =>
          this.componentService.loadComponent(meta.name),
        );
      }
    }

    debug('schemaFormsService', this.schemaFormsService);
  }

  /** Initialize Tenancy - Select or prompt for the active tenant */

  async initializeTenancy(): Promise<void> {
    const debug = Debug.extend('initializeTenancy');
    debug('initializing tenancy');
    const authenticated = await this.authService.getAuthenticated();
    debug('authenticated %j', authenticated);
    if (!authenticated) return;
    try {
      const tenants = await firstValueFrom(
        this.http.get<any[]>('/api/tenants/current-user'),
      );
      debug('tenants.length %j', tenants.length);

      const isAdmin = await this.authService.hasRole('Administrator');
      debug('isAdmin %j', isAdmin);

      this.showSwitchTenant = tenants.length > 1 || isAdmin;

      if (tenants.length === 1 && !isAdmin) {
        this.tenantService.setActiveTenant(tenants[0].id);
        this.currentTenantName = tenants[0].name;
        debug('auto-selected tenant %j', tenants[0].id);
        return;
      }

      const activeId = this.tenantService.getActiveTenantId();
      const active = activeId
        ? tenants.find((tenant) => tenant.id === activeId)
        : undefined;
      if (active) {
        this.currentTenantName = active.name;
      } else if (activeId) {
        // An administrator can operate in a tenant they are not a member of
        const tenant = await firstValueFrom(
          this.http.get<any>(`/api/tenants/${activeId}`),
        );
        this.currentTenantName = tenant?.name || '';
      } else if (isAdmin) {
        this.currentTenantName = 'All Tenants';
      }
      debug('currentTenantName %j', this.currentTenantName);

      // No selection is "All Tenants" for an administrator, so only prompt others
      if (!activeId && tenants.length > 1 && !isAdmin) {
        debug('opening tenant chooser');
        this.tenantChooserDialog.open();
      }
    } catch (error) {
      debug('error %O', error);
    }
  }

  /** Switch Tenant - Open the tenant chooser dialog */
  switchTenant(): void {
    const debug = Debug.extend('switchTenant');
    debug('switching tenant');
    this.tenantChooserDialog.open();
  }

  /** On Tenant Selected - Reload the current view with the new tenant */
  onTenantSelected(): void {
    const debug = Debug.extend('onTenantSelected');
    debug('tenant selected, reloading');
    window.location.reload();
  }

  ngOnInit(): void {
    this.initialize();
    this.registerFields();
    this.initializeTenancy();
  }
}

// Named seams below form AppComponent's public refinement surface. See
// docs/patterns/app-shell-seams.md before renaming any `name:` — a rename is a
// breaking refinement-surface change for downstream `app.component.patch.ts`.
